import { useQuery } from '@tanstack/react-query'
import Loading from 'egret/components/EgretLoadable/Loading'
import React from 'react'
import { queryDescribeObject, queryGetObjects } from './backend/Common'
import { FundingProgram } from './backend/salesforce-maps/FundingProgram'
import { FundingStream } from './backend/salesforce-maps/FundingStream'
import { GranteeBudgetCategoryMap } from './backend/salesforce-maps/GranteeBudgetCategory'
import { Network } from './backend/salesforce-maps/Network'
import { ReportRecordTypesMap } from './backend/salesforce-maps/ReportRecordTypes'
import { mapGranteeBudgetCategories } from './GlobalQueriesUtils'

interface FormEditorContextType {
  network: Network
  fundingStreams: FundingStream[]
  fundingPrograms: FundingProgram[]
  fundingProgramsIsLoading: boolean
  refetchFundingStreams: () => Promise<any>
  budgetCategories: GranteeBudgetCategoryMap | object | undefined
  reportRecordTypes: ReportRecordTypesMap | undefined
}

const GlobalQueriesContext = React.createContext<FormEditorContextType>({})

export function useGlobalQueries (): FormEditorContextType {
  const value = React.useContext(GlobalQueriesContext)
  if (value == null) {
    throw new Error(
      'useGlobalQueries must be wrapped in a <GlobalQueriesContextProvider />'
    )
  }
  return value
}

export function GlobalQueriesContextProvider (
  props: React.PropsWithChildren<FormEditorContextType>
): JSX.Element {
  const networkQuery = useQuery<Network>({
    queryKey: ['network'],
    refetchOnWindowFocus: false,
    queryFn: async () => await queryGetObjects({
      type: 'Network',
      select: ['Id', 'Name'],
      searchProps: {
        Name: 'APIConnectionPortal'
      }
    }).then(result => result[0])
  })

  const fundingStreamsQuery = useQuery<FundingStream[]>({
    queryKey: ['fundingStreams'],
    refetchOnWindowFocus: false,
    queryFn: async () => await queryGetObjects({
      type: 'Funding Stream',
      select: [
        '*',
        'Full_Name__r.*',
        'Funding_Program__r.*',
        'Funding_Program__r.Full_name__r.*',
        'Short_description__r.*',
        'Stream_name_only__r.*',
        'More_info_url__r.*'
      ]
    })
  })

  const fundingProgramsQuery = useQuery<FundingProgram[]>({
    queryKey: ['fundingPrograms'],
    refetchOnWindowFocus: false,
    queryFn: async () => await queryGetObjects({
      type: 'Funding Program',
      select: [
        'Id',
        'Program_overview_form__c',
        'Full_name__r.*',
        'Name'
      ]
    })
  })

  const reportRecordTypesQuery = useQuery<ReportRecordTypesMap>({
    queryKey: ['reportRecordTypes'],
    refetchOnWindowFocus: false,
    queryFn: async () => await queryDescribeObject({
      type: 'Report Record Types'
    }).then(result => {
      const ret = {}
      for (const item of result.recordTypeInfos) {
        ret[item.developerName] = item
      }
      return ret
    })
  })

  const budgetCategoriesQuery = useQuery<GranteeBudgetCategoryMap>({
    queryKey: ['granteeBudgetCategory'],
    refetchOnWindowFocus: false,
    queryFn: async () => await queryGetObjects({
      type: 'Grantee Budget Category',
      select: ['Id', 'Name', 'FGM_Portal__Parent_Category__c', 'Order__c']
    }).then(results => mapGranteeBudgetCategories(results))
  })

  // TODO: Add this check only in tabs using funding stream
  if (fundingStreamsQuery.data == null || networkQuery.data == null) {
    return <Loading atCenter />
  }

  return (
    <GlobalQueriesContext.Provider
      value={{
        network: networkQuery.data,
        fundingStreams: fundingStreamsQuery.data ?? [],
        refetchFundingStreams: fundingStreamsQuery.refetch,
        fundingPrograms: fundingProgramsQuery.data ?? [],
        fundingProgramsIsLoading: fundingProgramsQuery.isLoading,
        reportRecordTypes: reportRecordTypesQuery.data ?? {},
        budgetCategories: budgetCategoriesQuery.data ?? {}
      }}
    >
      {props.children}
    </GlobalQueriesContext.Provider>
  )
}
